import { emitter } from 'shared/helpers/mitt';
import { viewingInternalChatRoom } from './internalChatVisibility';
import WindowVisibilityHelper from './AudioAlerts/WindowVisibilityHelper';
import { getTotalUnreadCount } from './unreadCountHelper';
import {
  showDesktopNotification,
  translateDesktopNotification,
} from './desktopNotificationHelper';

function isRoomMuted(room) {
  const mutedUntil = room?.user_membership?.muted_until;
  if (!mutedUntil) return false;
  return new Date(mutedUntil) > new Date();
}

function truncate(text, max = 140) {
  if (!text || typeof text !== 'string') return '';
  const t = text.trim();
  return t.length <= max ? t : `${t.slice(0, max)}…`;
}

/**
 * In-app toast (bottom-right) for new internal chat messages — same stack as dashboard snackbars.
 */
export function notifyInternalChatMessageIfNeeded(store, data) {
  const message = data?.message;
  const room = data?.room;
  if (!message || !room?.id) return;

  const currentUserId = Number(store.getters.getCurrentUserID);
  if (Number(message.sender_id) === currentUserId) return;

  const roomState = store.getters['internalChat/getRoomById'](room.id);
  if (!roomState || isRoomMuted(roomState)) return;

  if (viewingInternalChatRoom(store.state.internalChat, room.id)) {
    return;
  }

  const accountId = store.getters.getCurrentAccountId;
  if (!accountId) return;

  const hasAttachments = Boolean(message.attachments?.length);
  const preview =
    truncate(message.content || '') ||
    (hasAttachments
      ? translateDesktopNotification('DESKTOP_NOTIFICATION.AUDIO_MESSAGE')
      : translateDesktopNotification('INTERNAL_CHAT_TOAST.FALLBACK_PREVIEW'));
  const unreadCount = getTotalUnreadCount(store);
  const conversationPath = `/app/accounts/${accountId}/internal-chat?room=${room.id}&message=${message.id}`;

  emitter.emit('newToastMessage', {
    message: preview,
    action: {
      titleI18n: {
        key: 'INTERNAL_CHAT_TOAST.TITLE',
        values: {
          sender: message.sender?.name || message.sender?.email || null,
          room: roomState.name || room.name || null,
        },
        fallbackKeys: {
          sender: 'INTERNAL_CHAT_TOAST.FALLBACK_SENDER',
          room: 'INTERNAL_CHAT_TOAST.FALLBACK_ROOM',
        },
      },
      messageFallbackKey: 'INTERNAL_CHAT_TOAST.FALLBACK_PREVIEW',
      placement: 'bottom-end',
      duration: 8000,
      type: 'link',
      to: {
        name: 'internal_chat',
        params: { accountId: String(accountId) },
        query: { room: String(room.id), message: String(message.id) },
      },
      usei18nLink: true,
      linkLabelKey: 'INTERNAL_CHAT_TOAST.OPEN_ACTION',
    },
  });

  if (WindowVisibilityHelper.isWindowVisible()) return;

  const senderName =
    message.sender?.name ||
    message.sender?.email ||
    translateDesktopNotification('INTERNAL_CHAT_TOAST.FALLBACK_SENDER');
  const roomName =
    roomState.name ||
    room.name ||
    translateDesktopNotification('INTERNAL_CHAT_TOAST.FALLBACK_ROOM');

  showDesktopNotification({
    title: roomState.kind === 'dm' || room.kind === 'dm' ? senderName : roomName,
    body:
      preview ||
      translateDesktopNotification('DESKTOP_NOTIFICATION.AUDIO_MESSAGE'),
    unreadCount,
    tag: `internal_chat_message_${room.id}`,
    url: conversationPath,
  });
}
